import { useEffect, useState } from 'react';
import {
  clubsBookingApi,
  type AdminReferralOverview,
  type AdminSubscriptionAccessStatus,
} from '@/api/clubs-booking';
import AdminShell from '@/components/admin/AdminShell';

const BillingPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busyAction, setBusyAction] = useState<'portal' | null>(null);
  const [copiedReferralLink, setCopiedReferralLink] = useState(false);
  const [referrals, setReferrals] = useState<AdminReferralOverview | null>(null);
  const [subscriptionAccess, setSubscriptionAccess] = useState<AdminSubscriptionAccessStatus | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [referralData, subscriptionData] = await Promise.all([
          clubsBookingApi.getAdminReferralOverview(),
          clubsBookingApi.getAdminSubscriptionAccessStatus(true, true),
        ]);
        if (!active) return;
        setReferrals(referralData);
        setSubscriptionAccess(subscriptionData);
      } catch (err: any) {
        if (!active) return;
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Failed to load billing details');
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, []);

  const currentPlanLabel = subscriptionAccess?.trialActive
    ? 'Free Trial'
    : subscriptionAccess?.hasActiveSubscription
    ? 'Standard Plan'
    : 'No active plan';

  const openSubscriptionPortal = async () => {
    setBusyAction('portal');
    setError('');
    try {
      const { url } = await clubsBookingApi.createAdminSubscriptionPortalLink({
        returnUrl: `${window.location.origin}/billing`,
      });
      window.location.assign(url);
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not open Stripe portal');
      setBusyAction(null);
    }
  };

  return (
    <AdminShell title="Billing" subtitle="Manage your Session Monkey subscription and referrals">
      {loading && <p className="mb-4 text-sm text-slate-500">Loading billing details...</p>}
      {(error || message) && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
            error
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700'
          }`}
        >
          {error || message}
        </div>
      )}

      <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-slate-800">Session Monkey Subscription</p>
              <p className="mt-1 text-xs text-slate-500">Session Monkey uses Stripe to manage your monthly subscription to use Session Monkey. You can manage your subscription using the link below.</p>
            </div>
            <p className="text-xs font-medium text-slate-700">
              Current plan: <span className="font-semibold">{currentPlanLabel}</span>
              {subscriptionAccess?.hasActiveSubscription && subscriptionAccess?.cancelAtPeriodEnd && (
                <span className="ml-1 font-semibold text-red-600">(cancelling)</span>
              )}
            </p>
          </div>
          <button
            type="button"
            className="mt-3 rounded-lg bg-[#1F8F5F] px-3 py-2 text-sm font-medium text-white hover:bg-[#17754D] disabled:opacity-60"
            onClick={() => void openSubscriptionPortal()}
            disabled={busyAction === 'portal'}
          >
            {busyAction === 'portal' ? 'Opening...' : 'Manage subscription in Stripe'}
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-slate-800">Referral tracking</p>
              <p className="text-xs text-slate-500">Referrals are tracked separately from Stripe discount codes.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              {referrals?.totalReferredAccounts || 0} referred
            </span>
          </div>

          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Your referral code</p>
            <p className="mt-1 text-base font-semibold text-slate-900">{referrals?.referralCode || 'Loading...'}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                disabled={!referrals?.referralCode}
                onClick={async () => {
                  if (!referrals?.referralCode) return;
                  const referralLink = `${window.location.origin}/login?mode=signup&ref=${encodeURIComponent(referrals.referralCode)}`;
                  try {
                    await navigator.clipboard.writeText(referralLink);
                  } catch {
                    return;
                  }
                  setCopiedReferralLink(true);
                  setMessage('Referral link copied.');
                  window.setTimeout(() => {
                    setCopiedReferralLink(false);
                    setMessage('');
                  }, 2000);
                }}
              >
                {copiedReferralLink ? 'Copied link' : 'Copy referral link'}
              </button>
              <p className="text-xs text-slate-500">
                Active subscribers from referrals: {referrals?.activeSubscriberReferrals || 0}
              </p>
            </div>
          </div>

          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-[560px] w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Joined</th>
                  <th className="px-3 py-2">Subscription</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
                {(referrals?.items || []).map((item) => (
                  <tr key={item.id}>
                    <td className="px-3 py-2">{item.name}</td>
                    <td className="px-3 py-2">{item.email}</td>
                    <td className="px-3 py-2">{new Date(item.createdAt).toLocaleDateString('en-GB')}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 font-medium ${item.hasActiveSubscription ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {item.hasActiveSubscription ? 'Active' : item.subscriptionStatus || 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
                {!referrals?.items?.length && (
                  <tr>
                    <td className="px-3 py-3 text-slate-500" colSpan={4}>No referred signups yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </AdminShell>
  );
};

export default BillingPage;
